import {useTranslation} from "react-i18next";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronDown, faChevronRight, faCodeBranch} from "@fortawesome/free-solid-svg-icons";
import FlintLoadingWrapper from "@components/page_components/common/loading/FlintLoadingWrapper";
import {OrderRecipeLineageAssignmentModel, OrderRecipeLineageNodeModel} from "@pages/orders/order.models";
import {useOrderRecipeLineagePanel, OrderRecipeLineagePanelProps} from "@pages/orders/preview/recipes/edit/lineage/useOrderRecipeLineagePanel";
import styles from "./OrderRecipeLineagePanel.module.scss";

function formatDate(value: string): string {
    return new Date(value).toLocaleString('sv').slice(0, 16);
}

function assignmentTypeBadge(assignmentType: string, t: (key: string) => string) {
    if (assignmentType === "LiveLink") return <span className="badge bg-info">{t("ORDER_RECIPE_LINEAGE_StatusLiveLink")}</span>;
    if (assignmentType === "DetachedCopy") return <span className="badge bg-warning">{t("ORDER_RECIPE_LINEAGE_StatusDetachedCopy")}</span>;
    return <span className="badge bg-secondary">{t("ORDER_RECIPE_LINEAGE_StatusOriginal")}</span>;
}

function LineageNodeFields({node}: { node: OrderRecipeLineageNodeModel }) {
    return (
        <div className={styles.nodeFields}>
            {node.CodeGeneratorNumber && <span>{node.CodeGeneratorNumber}</span>}
            {node.OrderNumber && <span>{node.OrderNumber}</span>}
            {node.ColourStandardName && <span>{node.ColourStandardName}</span>}
            {node.CustomerName && <span>{node.CustomerName}</span>}
            {node.CreatedBy && <span>{node.CreatedBy}</span>}
            {node.CreatedOnUtc && <span>{formatDate(node.CreatedOnUtc)}</span>}
        </div>
    );
}

export default function OrderRecipeLineagePanel(props: OrderRecipeLineagePanelProps) {
    const {t} = useTranslation();
    const {state, handlers, utils} = useOrderRecipeLineagePanel(props);

    if (!utils.hasAssignment) return null;

    const lineage = state.lineage.get;

    return (
        <div>
            <div className={styles.header} onClick={handlers.handleToggleExpand}>
                <FontAwesomeIcon icon={faCodeBranch}/>
                {t("ORDER_RECIPE_LINEAGE_Title")}
                <FontAwesomeIcon className={styles.headerChevron}
                                 icon={state.isExpanded.get ? faChevronDown : faChevronRight}/>
            </div>

            {state.isExpanded.get && (
                <FlintLoadingWrapper showLoading={state.status.get === "loading"}>
                    {state.status.get === "loaded" && lineage && (
                        <div className={styles.body}>
                            <div className={styles.statusRow}>
                                {assignmentTypeBadge(lineage.AssignmentType, t)}
                            </div>

                            {lineage.AssignmentType === "LiveLink" && (
                                <>
                                    {lineage.Source && (
                                        <div className={styles.nodeBlock}>
                                            <span className={styles.nodeLabel}>{t("ORDER_RECIPE_LINEAGE_Source")}:</span>
                                            <LineageNodeFields node={lineage.Source}/>
                                        </div>
                                    )}
                                    {lineage.Root && (
                                        <div className={styles.nodeBlock}>
                                            <span className={styles.nodeLabel}>{t("ORDER_RECIPE_LINEAGE_Root")}:</span>
                                            <LineageNodeFields node={lineage.Root}/>
                                        </div>
                                    )}
                                    <div className={styles.hint}>{t("ORDER_RECIPE_LINEAGE_FollowsSourceHint")}</div>
                                </>
                            )}

                            {lineage.AssignmentType === "DetachedCopy" && (
                                <>
                                    {lineage.Source && (
                                        <div className={styles.nodeBlock}>
                                            <span className={styles.nodeLabel}>{t("ORDER_RECIPE_LINEAGE_Source")}:</span>
                                            <LineageNodeFields node={lineage.Source}/>
                                        </div>
                                    )}
                                    {lineage.Root && (
                                        <div className={styles.nodeBlock}>
                                            <span className={styles.nodeLabel}>{t("ORDER_RECIPE_LINEAGE_Root")}:</span>
                                            <LineageNodeFields node={lineage.Root}/>
                                        </div>
                                    )}
                                    <div className={styles.detachMeta}>
                                        {lineage.DetachedBy && (
                                            <span>
                                                <span className={styles.nodeLabel}>{t("ORDER_RECIPE_LINEAGE_DetachedBy")}:</span>
                                                {lineage.DetachedBy}
                                            </span>
                                        )}
                                        {lineage.DetachedOnUtc && (
                                            <span>
                                                <span className={styles.nodeLabel}>{t("ORDER_RECIPE_LINEAGE_DetachedOn")}:</span>
                                                {formatDate(lineage.DetachedOnUtc)}
                                            </span>
                                        )}
                                    </div>
                                    <div className={styles.hint}>{t("ORDER_RECIPE_LINEAGE_DetachedHint")}</div>
                                </>
                            )}

                            {lineage.AssignmentType !== "Original" && (
                                <div className={styles.relatedAssignments}>
                                    <div className={styles.relatedAssignmentsTitle}>
                                        {t("ORDER_RECIPE_LINEAGE_RelatedAssignments")}
                                    </div>
                                    {lineage.RelatedAssignments.length === 0 ? (
                                        <div>{t("ORDER_RECIPE_LINEAGE_NoRelatedAssignments")}</div>
                                    ) : (
                                        lineage.RelatedAssignments.map((a: OrderRecipeLineageAssignmentModel) => (
                                            <div key={a.AssignmentGuid} className={styles.relatedAssignmentRow}>
                                                {assignmentTypeBadge(a.AssignmentType, t)}
                                                <span>{a.OrderNumber}</span>
                                                {a.ColourStandardName && <span>{a.ColourStandardName}</span>}
                                                {a.CreatedBy && <span>{a.CreatedBy}</span>}
                                                {a.CreatedOnUtc && <span>{formatDate(a.CreatedOnUtc)}</span>}
                                                {a.DetachedBy && (
                                                    <span>
                                                        <span className={styles.nodeLabel}>{t("ORDER_RECIPE_LINEAGE_DetachedBy")}:</span>
                                                        {a.DetachedBy}
                                                    </span>
                                                )}
                                                {a.DetachedOnUtc && (
                                                    <span>
                                                        <span className={styles.nodeLabel}>{t("ORDER_RECIPE_LINEAGE_DetachedOn")}:</span>
                                                        {formatDate(a.DetachedOnUtc)}
                                                    </span>
                                                )}
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </FlintLoadingWrapper>
            )}
        </div>
    );
}
